import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const VISITOR_ID_KEY = 'museoVisitorId';
const VisitorContext = createContext();

const generateVisitorId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export function VisitorProvider({ children }) {
  const [visitorId, setVisitorId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      let id = null;
      try {
        id = localStorage.getItem(VISITOR_ID_KEY);
        if (!id) {
          id = generateVisitorId();
          localStorage.setItem(VISITOR_ID_KEY, id);
        }
      } catch {
        id = generateVisitorId();
      }
      setVisitorId(id);

      try {
        const ref = doc(db, 'visitors', id);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setProfile(snap.data());
          setDoc(ref, { lastSeenAt: serverTimestamp() }, { merge: true }).catch(() => {});
        } else {
          setProfile(null);
        }
      } catch (e) {
        console.error('Error loading visitor profile:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveProfile = useCallback(async (name, email) => {
    if (!visitorId) return;
    const nowData = {
      name: (name || '').trim() || 'Explorador',
      email: (email || '').trim(),
      createdAt: serverTimestamp(),
      lastSeenAt: serverTimestamp()
    };
    await setDoc(doc(db, 'visitors', visitorId), nowData, { merge: true });
    setProfile((prev) => ({ ...(prev || {}), ...nowData }));
  }, [visitorId]);

  const refreshProfile = useCallback(async () => {
    if (!visitorId) return;
    try {
      const snap = await getDoc(doc(db, 'visitors', visitorId));
      if (snap.exists()) setProfile(snap.data());
    } catch (e) {
      console.error('Error refreshing visitor profile:', e);
    }
  }, [visitorId]);

  const value = {
    visitorId,
    profile,
    loading,
    needsProfile: !loading && !!visitorId && !profile,
    saveProfile,
    refreshProfile
  };

  return (
    <VisitorContext.Provider value={value}>
      {children}
    </VisitorContext.Provider>
  );
}

export const useVisitor = () => {
  const context = useContext(VisitorContext);
  if (!context) {
    throw new Error('useVisitor debe ser usado dentro de un VisitorProvider');
  }
  return context;
};
